import { useSyncExternalStore } from 'react';

/**
 * Whether Autopostmate can be installed on this device, and the way to do it.
 *
 * Chromium browsers (Chrome, Edge, Android) fire `beforeinstallprompt` once
 * the app qualifies; the event's `prompt()` opens the browser's own install
 * dialog. The event can fire before React mounts, so `captureInstallPrompt`
 * runs from `main.tsx` and keeps it here. Its default is left alone: the
 * browser's own install icon and menu entry stay exactly as they are, and our
 * buttons are one more way in, not a replacement.
 *
 * Safari on iOS and iPadOS fires nothing and has no API: installing is Share,
 * then Add to Home Screen, which `InstallHelpSheet` spells out.
 *
 * Once running installed (`display-mode: standalone`), there is nothing to
 * offer and every entry point hides.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface InstallState {
  /** The browser handed us an install prompt we can open. */
  canPrompt: boolean;
  /** Running as the installed app. */
  isStandalone: boolean;
  /** iPhone or iPad: installed by hand from the Share menu. */
  isIos: boolean;
}

const STANDALONE_QUERY = '(display-mode: standalone)';

let deferred: BeforeInstallPromptEvent | null = null;
let state: InstallState = { canPrompt: false, isStandalone: false, isIos: false };
const listeners = new Set<() => void>();

function readStandalone(): boolean {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return window.matchMedia(STANDALONE_QUERY).matches || iosStandalone;
}

function readIos(): boolean {
  // iPadOS reports a desktop Mac user agent; a Mac with touch points is an iPad.
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1)
  );
}

function update(): void {
  state = { canPrompt: deferred !== null, isStandalone: readStandalone(), isIos: readIos() };
  listeners.forEach((listener) => listener());
}

/** Starts listening. Called once from `main.tsx`, before the app renders. */
export function captureInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    deferred = event as BeforeInstallPromptEvent;
    update();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    update();
  });
  window.matchMedia(STANDALONE_QUERY).addEventListener('change', update);
  update();
}

/**
 * Opens the browser's install dialog. The event is single use, so it is
 * dropped whatever the answer; the browser fires a new one if the app still
 * qualifies.
 */
async function prompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred;
  if (!event) return 'unavailable';
  deferred = null;
  update();
  await event.prompt();
  const choice = await event.userChoice;
  return choice.outcome;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): InstallState {
  return state;
}

export interface InstallPrompt extends InstallState {
  /** There is a way to install from here: our prompt, or the iOS steps. */
  isOffered: boolean;
  prompt: typeof prompt;
}

export function useInstallPrompt(): InstallPrompt {
  const current = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return {
    ...current,
    isOffered: !current.isStandalone && (current.canPrompt || current.isIos),
    prompt,
  };
}
